import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { useDashReport } from '../hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface EspecialidadDetalle {
  especialidad: string
  total: number
  es_numerico: boolean
  porcentaje: number
}

interface ProductoItem {
  nombre: string
  total: number
  porcentaje: number
  especialidades: EspecialidadDetalle[]
  total_especialidades: number
}

interface ResumenEspecialidad {
  especialidad: string
  total: number
  porcentaje: number
}

interface UnipharmBd0326Data {
  titulo: string
  subtitulo: string
  total_general: number
  total_productos: number
  total_especialidades: number
  productos: ProductoItem[]
  resumen_especialidades: ResumenEspecialidad[]
}

const COLORES_PRODUCTOS = [
  { bg: 'rgba(2, 132, 199, 0.85)', border: '#0284c7' },
  { bg: 'rgba(3, 155, 229, 0.85)', border: '#0399e5' },
  { bg: 'rgba(14, 165, 233, 0.85)', border: '#0ea5e9' },
  { bg: 'rgba(56, 189, 248, 0.85)', border: '#38bdf8' },
  { bg: 'rgba(100, 205, 250, 0.85)', border: '#64cdfa' },
  { bg: 'rgba(125, 211, 252, 0.85)', border: '#7dd3fc' },
]

const CARD_COLORS = ['#0284c7', '#0399e5', '#0ea5e9', '#38bdf8', '#64cdfa', '#7dd3fc']

const COLORES_ESPECIALIDADES = [
  { bg: 'rgba(99, 102, 241, 0.7)', border: '#6366f1' },
  { bg: 'rgba(139, 92, 246, 0.7)', border: '#8b5cf6' },
  { bg: 'rgba(168, 85, 247, 0.7)', border: '#a855f7' },
  { bg: 'rgba(217, 70, 239, 0.7)', border: '#d946ef' },
  { bg: 'rgba(236, 72, 153, 0.7)', border: '#ec4899' },
  { bg: 'rgba(244, 63, 94, 0.7)', border: '#f43e5e' },
  { bg: 'rgba(251, 113, 133, 0.7)', border: '#fb7185' },
  { bg: 'rgba(253, 164, 175, 0.7)', border: '#fda4af' },
  { bg: 'rgba(34, 211, 238, 0.7)', border: '#22d3ee' },
  { bg: 'rgba(45, 212, 191, 0.7)', border: '#2dd4bf' },
]

const LOGO_URL = '/logo-tonal-negro.png'

export function UnipharmBd0326Page() {
  const { data, isLoading, error } = useDashReport<UnipharmBd0326Data>('unipharma/bd-0326')
  const [productoActivo, setProductoActivo] = useState<number | null>(null)
  const [espSeleccionadas, setEspSeleccionadas] = useState<number[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Animación de la tabla colapsable
  useEffect(() => {
    if (contentRef.current) {
      if (expanded) {
        contentRef.current.style.maxHeight = contentRef.current.scrollHeight + 'px'
        contentRef.current.style.opacity = '1'
      } else {
        contentRef.current.style.maxHeight = '0px'
        contentRef.current.style.opacity = '0'
      }
    }
  }, [expanded, productoActivo, espSeleccionadas])

  const filtrarProducto = useCallback((idx: number | null) => {
    setProductoActivo(idx)
    // Limpiar especialidades que no aplican al nuevo producto
    if (idx !== null && data) {
      const prod = data.productos[idx]
      setEspSeleccionadas(prev => prev.filter(espIdx => {
        const espNombre = data.resumen_especialidades[espIdx].especialidad
        return prod.especialidades.some(e => e.especialidad === espNombre && e.es_numerico)
      }))
    }
  }, [data])

  const toggleEspecialidad = useCallback((espIdx: number) => {
    setEspSeleccionadas(prev =>
      prev.includes(espIdx) ? prev.filter(i => i !== espIdx) : [...prev, espIdx]
    )
  }, [])

  const limpiarEspecialidades = useCallback(() => {
    setEspSeleccionadas([])
    setDropdownOpen(false)
  }, [])

  // Calcular datos filtrados
  const filteredData = useMemo(() => {
    if (!data) return null

    const productos = data.productos
    const resumenEspecialidades = data.resumen_especialidades
    const totalGeneral = data.total_general
    const totalEspecialidades = resumenEspecialidades.length

    const hayFiltroEsp = espSeleccionadas.length > 0
    const nombresEsp = espSeleccionadas.map(i => resumenEspecialidades[i].especialidad)

    // Determinar productos visibles
    let prodsVisibles: number[]
    if (productoActivo !== null) {
      prodsVisibles = [productoActivo]
    } else {
      prodsVisibles = productos.map((_, i) => i)
    }

    // Si hay filtro de especialidad, filtrar productos que tengan al menos una
    if (hayFiltroEsp) {
      prodsVisibles = prodsVisibles.filter(i => {
        return productos[i].especialidades.some(e => e.es_numerico && nombresEsp.includes(e.especialidad))
      })
    }

    // Calcular datos filtrados por producto
    const datosFiltrados = prodsVisibles.map(i => {
      const prod = productos[i]
      let esps = prod.especialidades.filter(e => e.es_numerico)
      if (hayFiltroEsp) {
        esps = esps.filter(e => nombresEsp.includes(e.especialidad))
      }
      const total = esps.reduce((sum, e) => sum + e.total, 0)
      return { nombre: prod.nombre, total, esps, idx: i }
    })

    const totalFiltrado = hayFiltroEsp
      ? espSeleccionadas.reduce((sum, i) => sum + resumenEspecialidades[i].total, 0)
      : (productoActivo !== null ? productos[productoActivo].total : totalGeneral)

    // Determinar especialidades visibles para la tabla
    let espsParaTabla: { nombre: string; total: number }[]
    if (productoActivo !== null && !hayFiltroEsp) {
      espsParaTabla = productos[productoActivo].especialidades
        .filter(e => e.es_numerico)
        .map(e => ({ nombre: e.especialidad, total: e.total }))
    } else if (hayFiltroEsp) {
      espsParaTabla = espSeleccionadas.map(i => ({
        nombre: resumenEspecialidades[i].especialidad,
        total: resumenEspecialidades[i].total,
      }))
    } else {
      espsParaTabla = resumenEspecialidades.map(e => ({
        nombre: e.especialidad,
        total: e.total,
      }))
    }

    // Titulo
    let titulo = 'Resumen General'
    let labelTotal = 'Total Base de Datos'
    if (productoActivo !== null && hayFiltroEsp) {
      titulo = productos[productoActivo].nombre + ' - ' + (espSeleccionadas.length === 1 ? nombresEsp[0] : espSeleccionadas.length + ' esp.')
      labelTotal = 'Total filtrado'
    } else if (productoActivo !== null) {
      titulo = productos[productoActivo].nombre
      labelTotal = 'Total ' + productos[productoActivo].nombre
    } else if (hayFiltroEsp) {
      titulo = espSeleccionadas.length === 1 ? nombresEsp[0] : espSeleccionadas.length + ' especialidades'
      labelTotal = 'Total selección'
    }

    // Num especialidades
    const numEsps = hayFiltroEsp
      ? espSeleccionadas.length
      : (productoActivo !== null ? productos[productoActivo].especialidades.filter(e => e.es_numerico).length : totalEspecialidades)

    // Chart data
    let chartLabels: string[]
    let chartValores: number[]
    let chartColores: { bg: string; border: string }[]
    let chartTitulo: string

    if (datosFiltrados.length === 1 && !hayFiltroEsp) {
      const d = datosFiltrados[0]
      chartLabels = d.esps.map(e => e.especialidad)
      chartValores = d.esps.map(e => e.total)
      chartColores = COLORES_ESPECIALIDADES
      chartTitulo = 'Especialidades - ' + d.nombre
    } else {
      chartLabels = datosFiltrados.map(d => d.nombre)
      chartValores = datosFiltrados.map(d => d.total)
      chartColores = COLORES_PRODUCTOS
      chartTitulo = titulo + ' por Producto'
    }

    // Tabla total
    const totalTabla = espsParaTabla.reduce((sum, e) => sum + e.total, 0)
    const filasTabla = espsParaTabla.map(e => ({
      nombre: e.nombre,
      total: e.total,
      porcentaje: totalTabla > 0 ? Math.round(e.total / totalTabla * 1000) / 10 : 0,
    }))

    // Opciones visibles del dropdown
    const opcionesDropdown = resumenEspecialidades.map((esp, idx) => {
      if (productoActivo === null) {
        return { visible: true, idx, nombre: esp.especialidad, total: esp.total }
      }
      const prod = productos[productoActivo]
      const tieneEsp = prod.especialidades.some(e => e.especialidad === esp.especialidad && e.es_numerico)
      return { visible: tieneEsp, idx, nombre: esp.especialidad, total: esp.total }
    })

    return {
      titulo,
      labelTotal,
      totalFiltrado,
      prodsVisibles,
      datosFiltrados,
      numEsps,
      chartLabels,
      chartValores,
      chartColores,
      chartTitulo,
      filasTabla,
      totalTabla,
      opcionesDropdown,
    }
  }, [data, productoActivo, espSeleccionadas])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !filteredData) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const {
    titulo,
    labelTotal,
    totalFiltrado,
    prodsVisibles,
    datosFiltrados,
    numEsps,
    chartLabels,
    chartValores,
    chartColores,
    chartTitulo,
    filasTabla,
    totalTabla,
    opcionesDropdown,
  } = filteredData

  // Dropdown label
  let dropdownLabel = 'Seleccionar especialidades'
  if (espSeleccionadas.length === 1) {
    dropdownLabel = data.resumen_especialidades[espSeleccionadas[0]].especialidad
  } else if (espSeleccionadas.length > 1) {
    dropdownLabel = espSeleccionadas.length + ' especialidades'
  }

  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Total',
      data: chartValores,
      backgroundColor: chartLabels.map((_, i) => chartColores[i % chartColores.length].bg),
      borderColor: chartLabels.map((_, i) => chartColores[i % chartColores.length].border),
      borderWidth: 1,
    }],
  }

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 80 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 as const },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC')
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  return (
    <>
      <style>{`
        .filter-btn.active {
          background-color: #2563eb !important;
          color: white !important;
        }
        .filter-btn.active:hover {
          background-color: #1d4ed8 !important;
        }
        .filter-btn:not(.active):hover {
          background-color: #e5e7eb;
        }
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
      `}</style>
      <div className="bg-gray-100 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img src={LOGO_URL} alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noreferrer"
                   className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                  Calculador
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            {/* Por producto */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
              <div className="flex items-center shrink-0">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="font-semibold text-gray-900">Producto:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => filtrarProducto(null)}
                  className={`filter-btn ${productoActivo === null ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 cursor-pointer`}
                >
                  Todos
                </button>
                {data.productos.map((prod, idx) => (
                  <button
                    key={idx}
                    onClick={() => filtrarProducto(idx)}
                    className={`filter-btn ${productoActivo === idx ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 cursor-pointer`}
                  >
                    {prod.nombre}
                  </button>
                ))}
              </div>
            </div>
            {/* Por especialidad (dropdown multi-select) */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center shrink-0">
                <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-semibold text-gray-900">Especialidad:</span>
              </div>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  type="button"
                  className="inline-flex items-center justify-between min-w-[240px] px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                >
                  <span>{dropdownLabel}</span>
                  <svg className="w-4 h-4 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute z-20 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {opcionesDropdown.map(opt => (
                      <label
                        key={opt.idx}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        style={{ display: opt.visible ? 'flex' : 'none' }}
                      >
                        <input
                          type="checkbox"
                          checked={espSeleccionadas.includes(opt.idx)}
                          onChange={() => toggleEspecialidad(opt.idx)}
                          className="esp-checkbox mr-3 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">{opt.nombre}</span>
                        <span className="ml-auto text-gray-400 text-xs">{opt.total}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={limpiarEspecialidades}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer shrink-0"
              >
                Limpiar
              </button>
            </div>
          </div>

          {/* Estadisticas Generales */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>{titulo}</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">{totalFiltrado.toLocaleString('es-EC')}</p>
                <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl lg:text-3xl font-bold text-indigo-600">{prodsVisibles.length}</p>
                <p className="text-sm text-gray-600 mt-1">{prodsVisibles.length === 1 ? 'Producto' : 'Productos'}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl lg:text-3xl font-bold text-purple-600">{numEsps}</p>
                <p className="text-sm text-gray-600 mt-1">{numEsps === 1 ? 'Especialidad' : 'Especialidades'}</p>
              </div>
            </div>

            {/* Cards de productos */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {data.productos.map((prod, i) => {
                const dato = datosFiltrados.find(d => d.idx === i)
                if (!dato) return null
                return (
                  <div key={i} className="bg-white rounded-lg px-3 py-3 text-center shadow-sm">
                    <p className="text-lg lg:text-xl font-bold" style={{ color: CARD_COLORS[i % CARD_COLORS.length] }}>
                      {dato.total.toLocaleString('es-EC')}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-medium">{prod.nombre}</p>
                    <p className="text-xs text-gray-400">
                      {totalFiltrado > 0 ? Math.round(dato.total / totalFiltrado * 1000) / 10 : 0}%
                    </p>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Grafica de Barras */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>{chartTitulo}</span>
              </h3>
              <div className="h-64 lg:h-80">
                <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>
          </div>

          {/* Tabla de Detalle */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-base lg:text-lg">{'Detalle - ' + titulo}</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${expanded ? 'expanded' : 'animate-pulse-subtle'}`}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filasTabla.map((fila, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{fila.nombre}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">{fila.total.toLocaleString('es-EC')}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">{fila.porcentaje}%</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{totalTabla.toLocaleString('es-EC')}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2026 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
